import { createTRPCProxyClient, httpBatchLink, type TRPCLink } from '@trpc/client'
import { observable } from '@trpc/server/observable'
import type { VaultRouter } from '@vault/server'
import { isClientOutdatedError, PROTOCOL_VERSION_HEADER, VAULT_PROTOCOL_VERSION } from '@vault/shared'
import { getApiAuthToken } from './runtime'
import { broadcastVaultEvent } from './vaultEvents'
import { withRequestTimeout } from './requestTimeout'

let vaultEndpoint = ''
let vaultTrpcClient: ReturnType<typeof createTRPCProxyClient<VaultRouter>> | null = null
let untrackedVaultTrpcClient: ReturnType<typeof createTRPCProxyClient<VaultRouter>> | null = null

/**
 * Sets the vault API base URL (the tRPC handler is served at `${endpoint}/trpc`).
 * Each realm (main thread and sync worker) must configure it before making requests.
 */
export function configureVaultEndpoint(endpoint: string) {
  vaultEndpoint = endpoint.replace(/\/$/, '')
}

function getVaultTrpcUrl() {
  return `${vaultEndpoint}/trpc`
}

/** Announces errors that say this build is too old, so the host app can update (see `onClientOutdated`) */
function clientOutdatedLink(): TRPCLink<VaultRouter> {
  return () => ({ next, op }) => observable(observer => next(op).subscribe({
    next: value => observer.next(value),
    error: error => {
      if (isClientOutdatedError(error)) {
        broadcastVaultEvent({ type: 'CLIENT_OUTDATED' })
      }
      observer.error(error)
    },
    complete: () => observer.complete(),
  }))
}

/**
 * Links for a tRPC client of the vault's server, including app routers built on the same tRPC
 * instance. They send the realm's session token (`getApiAuthToken`) and this build's protocol
 * version, report when the server says the build is too old, and fail requests that get no
 * response in time (see `withRequestTimeout`).
 */
export function createVaultLinks(
  { url = getVaultTrpcUrl(), customFetch }: {
    url?: string,
    customFetch?: typeof fetch,
  } = {},
): TRPCLink<VaultRouter>[] {
  return [
    clientOutdatedLink(),
    httpBatchLink({
      url,
      headers: () => {
        const token = getApiAuthToken()
        return {
          [PROTOCOL_VERSION_HEADER]: String(VAULT_PROTOCOL_VERSION),
          ...(token ? { Authorization: `Basic ${token}` } : {}),
        }
      },
      fetch: withRequestTimeout(customFetch ?? ((input, init) => fetch(input, init))),
    }),
  ]
}

export function initVaultTrpcClient(customFetch: typeof fetch) {
  vaultTrpcClient = createTRPCProxyClient<VaultRouter>({
    links: createVaultLinks({ customFetch }),
  })
}

export function getVaultTrpcClient() {
  if (!vaultTrpcClient) {
    throw new Error('Vault tRPC client not initialized. Call initVaultTrpcClient with a fetch implementation first.')
  }
  return vaultTrpcClient
}

/**
 * The client for the sync worker's background requests (polls and snapshot uploads). It skips the
 * tracked fetch, so these requests don't show as activity in the sync status. Like the other
 * clients, it sends the realm's current session token.
 */
export function getUntrackedVaultTrpcClient() {
  untrackedVaultTrpcClient ??= createTRPCProxyClient<VaultRouter>({
    links: createVaultLinks(),
  })
  return untrackedVaultTrpcClient
}
